import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronIcon } from "../icons/FormIcons";

const CollapsibleSection = ({
  title,
  icon,
  children,
  defaultOpen = false,
  badge,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const innerRef = useRef(null); // the actual content
  const [maxHeight, setMaxHeight] = useState(defaultOpen ? "none" : "0px");

  const recalc = () => {
    const el = innerRef.current;
    if (!el) return;
    // Use scrollHeight so we always allow full content (tasks, docs, etc.)
    setMaxHeight(`${el.scrollHeight}px`);
  };

  // When opening/closing, recalc immediately so maxHeight matches content.
  useLayoutEffect(() => {
    if (!isOpen) {
      setMaxHeight("0px");
      return;
    }
    recalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // While open, keep maxHeight in sync as content grows (e.g., adding tasks).
  useEffect(() => {
    if (!isOpen) return;
    const el = innerRef.current;
    if (!el) return;

    recalc();

    const ro = new ResizeObserver(() => recalc());
    ro.observe(el);

    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="w-full px-8 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
            {icon}
          </div>
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          {badge && (
            <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
              {badge}
            </span>
          )}
        </div>

        <ChevronIcon
          className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <div
        className="overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out"
        style={{
          maxHeight: isOpen ? maxHeight : "0px",
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div ref={innerRef} className="px-8 pb-8 pt-4">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
